import NoImage from 'assets/images/noimage.png'
import BreadCrumbs from 'components/breadcrumbs'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBTabSwitcher from 'components/PMBTabSwitcher'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { capitalizeEveryWord, useDebounce } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ChevronDown, Edit2, Eye, Plus, Trash2 } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Button, Card, CardBody, Spinner } from 'reactstrap'
import { formatDatePMB } from 'utils/formatDate'
import { URL_SETTINGS, URL_WORKPLACE } from 'utils/pathUrl'
import {
  useDeleteMasterItemMutation,
  useFetchMasterItemsQuery,
} from './masterItemsApiSlice'
import { setFetchOption } from './masterItemsSlice'
import ModalAddEditMasterItem from './ModalAddEditMasterItem'
import ModalDetailMasterItem from './ModalDetailMasterItem'
import SchemaList from './SchemaList'

const ListMasterItems = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const location = useLocation()

  const { tag } = useSelector((state) => state.masterItems)

  const isMobile = useIsMobile()

  const isFromWorkplace = location.state?.from
    ? location.state.from === 'workplace'
    : document.referrer.includes(URL_WORKPLACE) ||
      sessionStorage.getItem('masterItemsFrom') === 'workplace'

  useEffect(() => {
    if (location.state?.from === 'workplace') {
      sessionStorage.setItem('masterItemsFrom', 'workplace')
    } else if (location.state?.from === 'settings') {
      sessionStorage.setItem('masterItemsFrom', 'settings')
    }
  }, [location.state?.from])

  const breadcrumbParent = isFromWorkplace ? 'Alat Bantu' : 'Pengaturan'
  const breadcrumbParentLink = isFromWorkplace ? URL_WORKPLACE : URL_SETTINGS

  const [searchInput, setSearchInput] = useState('')
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [isFilter, setIsFilter] = useState(false)
  const [imageItems, setImagesItems] = useState([])
  const [modalOpenImage, setModalOpenImage] = useState(false)
  const [isOpenModalAddEdit, setIsOpenModalAddEdit] = useState(false)
  const [isOpenModalDetail, setIsOpenModalDetail] = useState(false)
  const [dataItemById, setDataItemById] = useState(null)
  const [activeTab, setActiveTab] = useState('komoditas')

  const [deleteMasterItem, { isLoading: isDeleting }] =
    useDeleteMasterItemMutation()

  useEffect(() => {
    dispatch(
      setFetchOption({
        page: 0,
        s: '',
      }),
    )
    setSearchInput('')
    setIsFilter(false)
  }, [activeTab])

  const { data, isLoading, refetch, isFetching } = useFetchMasterItemsQuery(
    {
      page: tag?.page ?? 0,
      size: tag?.size ?? 10,
      s: tag?.s,
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
      withSchema: tag?.withSchema,
    },
    { refetchOnMountOrArgChange: true, skip: activeTab !== 'komoditas' },
  )

  // Separate query for parent items dropdown with reasonable limit
  const { data: parentItemsData } = useFetchMasterItemsQuery(
    {
      page: 0,
      size: 100,
      sorty: 'name',
      sortdir: 'ASC',
    },
    { refetchOnMountOrArgChange: false },
  )

  const onHandleOpenImageModal = (imageUrls) => {
    setImagesItems([imageUrls])
    setModalOpenImage(true)
  }

  // COLUMNS
  const columns = [
    {
      name: <span>Tanggal</span>,
      selector: 'createdAt',
      sortable: true,
      sortField: 'createdAt',
      grow: 1,
      cell: (row) => {
        return (
          <span className="m-0">
            {formatDatePMB(row?.createdAt, row?.createdAt)}
          </span>
        )
      },
      minWidth: '150px',
    },
    {
      name: <span>Nama Barang</span>,
      selector: 'nameItem',
      sortable: true,
      sortField: 'nameItem',
      grow: 2,
      cell: (row, index) => {
        return (
          <div className="d-flex align-items-center gap-2 py-1">
            <img
              src={
                row?.linkFoto && row?.linkFoto.length > 0
                  ? row?.linkFoto
                  : NoImage
              }
              alt={row?.nameItem || `item ${index + 1}`}
              width={40}
              height={40}
              className="rounded"
              onClick={(e) => {
                e.stopPropagation()
                onHandleOpenImageModal(row?.linkFoto || NoImage)
              }}
              style={{ cursor: 'pointer', objectFit: 'cover', flexShrink: 0 }}
            />
            <span className="m-0">{row?.nameItem}</span>
          </div>
        )
      },
      minWidth: '250px',
    },
    {
      name: <span>Satuan</span>,
      selector: 'unit',
      sortable: true,
      sortField: 'unit',
      cell: (row) => {
        return <span className="m-0">{capitalizeEveryWord(row?.unit)}</span>
      },
      minWidth: '50px',
    },
    {
      name: <span className="fw-semibold"></span>,
      width: '160px',
      right: true,
      cell: (row) => {
        return (
          <div
            className="d-flex align-items-center justify-content-end"
            style={{ gap: '14px' }}
            data-testid={`master-item-table-row-${row?.id}`}
          >
            <div id={`view-${row?.id}`} data-testid={`master-item-row-view-${row?.id}`}>
              <Eye
                size={19}
                onClick={(e) => {
                  e.stopPropagation()
                  setDataItemById(row)
                  setIsOpenModalDetail(true)
                }}
                style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              />
            </div>
            <PMBTooltip
              target={`view-${row?.id}`}
              text="Lihat Detail & Skema"
            />

            <div id={`edit-${row?.id}`} data-testid={`master-item-row-edit-${row?.id}`}>
              <Edit2
                size={19}
                onClick={(e) => {
                  e.stopPropagation()
                  setDataItemById(row)
                  setIsOpenModalAddEdit(true)
                }}
                style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              />
            </div>
            <PMBTooltip target={`edit-${row?.id}`} text="Edit Item" />

            <div id={`remove-${row?.id}`} data-testid={`master-item-row-delete-${row?.id}`}>
              <Trash2
                size={19}
                onClick={(e) => {
                  e.stopPropagation()
                  setIsOpenDeleteModal(true)
                  setDataItemById(row)
                }}
                style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              />
            </div>
            <PMBTooltip target={`remove-${row?.id}`} text="Hapus Item" />
          </div>
        )
      },
    },
  ]

  // HANDLE SUBMIT REMOVE BY ID
  const handleSubmitRemoveById = async (id) => {
    try {
      if (!id) {
        enqueueSnackbar('Tidak ada data yang dipilih untuk dihapus', {
          variant: 'error',
        })
        return
      }

      const response = await deleteMasterItem(id)
      if (response && response?.data) {
        enqueueSnackbar(`Item "${dataItemById?.nameItem}" berhasil dihapus`, {
          variant: 'success',
        })
        setIsOpenDeleteModal(false)
        setDataItemById(null)
        refetch()
      }
    } catch (error) {
      enqueueSnackbar('Item gagal dihapus', {
        variant: 'error',
      })
    }
  }

  const handleSort = (column, direction) => {
    dispatch(
      setFetchOption({
        sortby: column.sortField,
        sortdir: direction?.toUpperCase(),
      }),
    )
  }

  useEffect(() => {
    dispatch(setFetchOption({ page: 0 }))
  }, [searchInput])

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFetchOption({
        page: 0,
        s: value,
      }),
    )
  }, 600)

  const onHandleSearchChange = (value) => {
    if (!value?.trim()) {
      setIsFilter(false)
    } else {
      setIsFilter(true)
    }
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && tag?.s.length === 0) {
      e.preventDefault()
    }
  }

  return (
    <div className="w-100 px-3">
      {/* MODAL IMAGE PREVIEW */}
      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={imageItems}
      />

      {/* MODAL ADD/EDIT MASTER ITEM */}
      <ModalAddEditMasterItem
        isOpen={isOpenModalAddEdit}
        toggle={() => {
          setIsOpenModalAddEdit((prev) => !prev)
          setDataItemById(null)
        }}
        onSuccess={refetch}
        dataItemById={dataItemById}
        parentItems={parentItemsData?.items || []}
      />

      {/* MODAL DETAIL MASTER ITEM */}
      <ModalDetailMasterItem
        isOpen={isOpenModalDetail}
        toggle={() => {
          setIsOpenModalDetail((prev) => !prev)
          setDataItemById(null)
        }}
        itemData={dataItemById}
        onSuccess={refetch}
      />

      {/* MODAL REMOVE MASTER ITEM */}
      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => {
          setIsOpenDeleteModal((prev) => !prev)
          setDataItemById(null)
        }}
        title={`Hapus Master Item`}
        dataId={dataItemById?.id}
        desc={`Apakah Anda yakin ingin menghapus item "${dataItemById?.nameItem}"?`}
        isLoading={isDeleting}
        onRemoveData={(id) => handleSubmitRemoveById(id)}
        buttonTitle="Hapus"
      />

      <div className="mt-3">
        <BreadCrumbs
          breadCrumbActive="Manajemen Barang & Skema"
          breadCrumbParent={breadcrumbParent}
          breadCrumbParentLink={breadcrumbParentLink}
        />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <PMBTabSwitcher
            activeTab={activeTab}
            tabs={[
              {
                key: 'komoditas',
                label: 'Barang',
              },
              {
                key: 'skema',
                label: 'Skema',
              },
            ]}
            onTabChange={(tab) => setActiveTab(tab)}
          />

          {activeTab === 'komoditas' && (
            <DataTable
              responsive
              persistTableHead
              defaultSortFieldId={2}
              defaultSortAsc={true}
              columns={columns}
              data={data?.items || []}
              progressPending={isFetching}
              paginationTotalRows={data?.totalItems}
              pagination
              paginationServer
              highlightOnHover
              pointerOnHover
              onRowClicked={(row) => {
                setDataItemById(row)
                setIsOpenModalDetail(true)
              }}
              customStyles={{
                rows: {
                  style: {
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                    },
                  },
                },
              }}
              paginationComponent={() => (
                <PMBPagination
                  rowCount={data?.totalItems || 0}
                  currentPage={tag?.page}
                  rowsPerPage={tag?.size}
                  setCurrentPage={(e) => {
                    dispatch(setFetchOption({ page: e }))
                  }}
                  setRowsPerPage={(e) => {
                    dispatch(setFetchOption({ size: e }))
                  }}
                />
              )}
              progressComponent={
                <div style={{ margin: '100px 0px' }}>
                  <Spinner color="primary" />
                </div>
              }
              onSort={handleSort}
              sortIcon={<ChevronDown />}
              sortServer
              subHeader={true}
              subHeaderComponent={
                <div className="w-100">
                  <div
                    className={`d-flex mb-1 ${isMobile ? 'flex-column' : 'justify-content-between align-items-center'}`}
                  >
                    <div className="mb-2">
                      <div
                        className={
                          isMobile
                            ? 'd-flex flex-column gap-3'
                            : 'd-flex justify-content-between align-items-end'
                        }
                      >
                        <div style={{ width: isMobile ? '100%' : 'auto' }}>
                          <label
                            className="form-label mb-2"
                            style={{ fontSize: '0.875rem', fontWeight: 500 }}
                          >
                            Cari Barang
                          </label>
                          <PMBSearchInputGroup
                            value={searchInput}
                            placeholder="Cari nama barang..."
                            onChange={(e) =>
                              onHandleSearchChange(e.target.value)
                            }
                            maxWidth={isMobile ? '100%' : '350px'}
                            onKeyDown={handleKeyDown}
                            testId="master-item-search-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        onClick={() => setIsOpenModalAddEdit(true)}
                        size="sm"
                        color="primary"
                        className={`d-flex align-items-center justify-content-center gap-2 ${isMobile ? 'w-100 py-2' : ''}`}
                        data-testid="master-item-add-button"
                      >
                        <Plus size={16} />
                        Tambah Item
                      </Button>
                    </div>
                  </div>
                </div>
              }
              noDataComponent={
                <p className="my-3">
                  {isFilter
                    ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                    : 'Belum ada master item yang tersedia'}
                </p>
              }
            />
          )}

          {activeTab === 'skema' && (
            <SchemaList isActive={activeTab === 'skema'} />
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default ListMasterItems
